import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck, Inbox, RefreshCw, UserPlus, UserCheck, Users, XCircle } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api.js';
import { getSocket } from '../../lib/socket.js';
import { fmtFromNow } from '../../lib/format.js';
import { FEED_KEY, FEED_SOCKET_EVENTS, badgeText, toneFor } from '../../lib/notificationFeed.js';

const ICONS = {
  sync_request: RefreshCw, sync_done: UserCheck, sync_declined: XCircle, sync_cancelled: XCircle,
  teammate_approval: Users, unread_texts: Inbox, lead_assignment: UserPlus, duplicate_lead: UserPlus, supervisor_disconnect: Users,
};

/**
 * Header bell: one place for everything that needs the agent — sync requests and
 * outcomes, teammate approvals, unread texts, new-lead alerts. Refreshes live on
 * socket events and every minute.
 * @returns {JSX.Element}
 */
export default function NotificationBell() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const { data } = useQuery({
    queryKey: FEED_KEY,
    queryFn: () => apiGet('/notifications/feed'),
    refetchInterval: 60_000,
    staleTime: 15_000,
  });
  const items = data?.items || [];
  const unread = data?.unreadCount || 0;

  const markRead = useMutation({
    mutationFn: () => apiPost('/notifications/read'),
    onSuccess: () => qc.invalidateQueries({ queryKey: FEED_KEY }),
  });

  useEffect(() => {
    const socket = getSocket();
    if (!socket?.on) return undefined;
    const refresh = () => qc.invalidateQueries({ queryKey: FEED_KEY });
    FEED_SOCKET_EVENTS.forEach((e) => socket.on(e, refresh));
    return () => FEED_SOCKET_EVENTS.forEach((e) => socket.off(e, refresh));
  }, [qc]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid="notification-bell"
        className="w-9 h-9 rounded-lg bg-civix-panelLight border border-civix-border/60 hover:border-civix-purple/60 transition flex items-center justify-center relative"
      >
        <Bell className="w-4 h-4 text-white/70" />
        {unread > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-[10px] font-bold leading-[18px] text-center text-white ring-2 ring-civix-bg"
          >
            {badgeText(unread)}
          </span>
        )}
      </button>

      {open && (
        <div role="dialog" aria-label="Notifications" className="absolute right-0 mt-2 w-[360px] max-w-[92vw] panel z-50 shadow-glow-purple overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-civix-border/60">
            <div className="text-sm font-semibold">Notifications</div>
            <button
              type="button"
              onClick={() => markRead.mutate()}
              disabled={!unread || markRead.isPending}
              className="text-[11px] text-civix-purpleSoft hover:text-white disabled:opacity-40 flex items-center gap-1"
            >
              <CheckCheck className="w-3.5 h-3.5" /> Mark all read
            </button>
          </div>
          <div className="max-h-[420px] overflow-y-auto divide-y divide-civix-border/40">
            {items.length === 0 && (
              <div className="px-4 py-10 text-center text-xs text-white/40" data-testid="notification-empty">You're all caught up.</div>
            )}
            {items.map((n) => {
              const Icon = ICONS[n.type] || Bell;
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => { setOpen(false); nav(n.link || '/dashboard'); }}
                  className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-civix-panelLight transition ${n.unread ? 'bg-civix-purple/5' : ''}`}
                >
                  <span className={`mt-0.5 w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${toneFor(n.type)}`}>
                    <Icon className="w-4 h-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{n.title}</span>
                      {n.unread && <span className="w-2 h-2 rounded-full bg-civix-purple shrink-0" aria-label="Unread" />}
                    </span>
                    <span className="block text-xs text-white/55 mt-0.5 line-clamp-2">{n.body}</span>
                    <span className="block text-[10px] text-white/35 mt-1">{fmtFromNow(n.at)}{n.actionable ? ' · needs action' : ''}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
